import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2,
  RefreshCw,
  Check,
  X,
  Building2,
  Mail,
  Phone,
  MapPin,
  Clock
} from 'lucide-react';

const SuppliersManager = () => {
  const tenantId = localStorage.getItem('tenant_id') || 'tenant-123';
  
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    payment_terms: 'Net 30',
    currency: 'USD',
    lead_time_days: 14,
    notes: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, [statusFilter]);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      // Use the new with-totals endpoint to get PO aggregates
      const url = statusFilter === 'all' 
        ? `/api/v1/suppliers/with-totals`
        : `/api/v1/suppliers/with-totals?status=${statusFilter}`;
      
      // api.get returns data directly (already unwrapped from response.data)
      const data = await api.get(url, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        // Update existing
        await api.put(`/api/v1/suppliers/${editingId}`, formData, {
          headers: { 'X-Tenant-ID': tenantId }
        });
        toast.success('✅ Supplier updated!', {
          description: `${formData.name} has been updated successfully`
        });
      } else {
        // Create new
        await api.post('/api/v1/suppliers', formData, {
          headers: { 'X-Tenant-ID': tenantId }
        });
        toast.success('🎉 Supplier created!', {
          description: `${formData.name} has been added to your suppliers`
        });
      }
      
      setShowAddForm(false);
      setEditingId(null);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error('Failed to save supplier', {
        description: error.response?.data?.detail || error.message || 'An error occurred'
      });
    }
  };

  const handleEdit = (supplier) => {
    setFormData({
      name: supplier.name || '',
      code: supplier.code || '',
      contact_name: supplier.contact_name || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      city: supplier.city || '',
      country: supplier.country || '',
      payment_terms: supplier.payment_terms || 'Net 30',
      currency: supplier.currency || 'USD',
      lead_time_days: supplier.lead_time_days || 14,
      notes: supplier.notes || ''
    });
    setEditingId(supplier.id);
    setShowAddForm(true);
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Are you sure you want to deactivate this supplier?')) return;
    
    try {
      await api.delete(`/api/v1/suppliers/${supplierId}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      toast.success('🗑️ Supplier deactivated', {
        description: 'Supplier has been moved to inactive status'
      });
      fetchSuppliers();
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast.error('Failed to deactivate supplier', {
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleActivate = async (supplierId) => {
    try {
      await api.post(`/api/v1/suppliers/${supplierId}/activate`, {}, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      toast.success('✅ Supplier activated', {
        description: 'Supplier is now active and available for use'
      });
      fetchSuppliers();
    } catch (error) {
      console.error('Error activating supplier:', error);
      toast.error('Failed to activate supplier', {
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      contact_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      payment_terms: 'Net 30',
      currency: 'USD',
      lead_time_days: 14,
      notes: ''
    });
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.country?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-3 bg-[#0f1729] min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <h1 className="text-lg font-semibold text-white">Suppliers</h1>
        <div className="flex gap-2">
          <Button
            onClick={() => { resetForm(); setEditingId(null); setShowAddForm(true); }}
            className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white gap-1"
          >
            <Plus className="w-3 h-3" />
            Add Supplier
          </Button>
          <Button
            onClick={fetchSuppliers}
            variant="outline"
            className="h-8 w-8 p-0 border-gray-600"
          >
            <RefreshCw className="w-3 h-3 text-gray-400" />
          </Button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex gap-1 mb-3">
        {['active', 'inactive', 'all'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              statusFilter === status
                ? 'bg-blue-600 text-white'
                : 'bg-[#1a2744] text-gray-400 hover:text-white hover:bg-[#243352]'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-500" />
          <Input
            placeholder="Search suppliers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-7 text-sm bg-[#1a2744] border-gray-700 text-white placeholder-gray-500"
          />
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-[#1a2744] rounded-lg p-3 mb-3">
          <h2 className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-3">
            {editingId ? 'Edit Supplier' : 'Add New Supplier'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Code</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="e.g., CWDC"
                  className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Contact Name</label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Phone</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Country</label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Payment Terms</label>
                <select
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({...formData, payment_terms: e.target.value})}
                  className="w-full h-8 py-1 px-2 text-sm bg-[#0f1729] border border-gray-700 text-white rounded"
                >
                  <option value="Net 30">Net 30</option>
                  <option value="Net 45">Net 45</option>
                  <option value="Net 60">Net 60</option>
                  <option value="Net 90">Net 90</option>
                  <option value="COD">COD</option>
                  <option value="Advance">Advance</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Lead Time (days)</label>
                <Input
                  type="number"
                  value={formData.lead_time_days}
                  onChange={(e) => setFormData({...formData, lead_time_days: parseInt(e.target.value) || 0})}
                  className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowAddForm(false); setEditingId(null); resetForm(); }}
                className="h-8 px-3 text-xs border-gray-600 text-gray-300"
              >
                <X className="w-3 h-3 mr-1" />
                Cancel
              </Button>
              <Button
                type="submit"
                className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700"
              >
                <Check className="w-3 h-3 mr-1" />
                {editingId ? 'Update' : 'Add'} Supplier
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Suppliers Table */}
      <div className="bg-[#1a2744] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0f1729]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">Supplier</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">Country</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">Contact</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">Payment</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">Lead Time</th>
                <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-400 uppercase tracking-wider">Total Spent</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-3 py-2 text-center text-[10px] font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-3 py-8 text-center">
                    <RefreshCw className="w-4 h-4 animate-spin text-blue-500 mx-auto" />
                    <span className="ml-2 text-gray-400 text-sm">Loading...</span>
                  </td>
                </tr>
              ) : filteredSuppliers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-3 py-8 text-center">
                    <Building2 className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                    <p className="text-gray-400 text-sm">No suppliers found</p>
                  </td>
                </tr>
              ) : (
                filteredSuppliers.map((supplier) => (
                  <tr key={supplier.id} className="hover:bg-[#0f1729]/50 transition-colors">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded bg-blue-600/20 flex items-center justify-center">
                          <Building2 className="w-3.5 h-3.5 text-blue-400" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-white block">{supplier.name}</span>
                          {supplier.code && (
                            <span className="text-[10px] text-gray-500">{supplier.code}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-300">
                      {supplier.country || '-'}
                    </td>
                    <td className="px-3 py-2">
                      {supplier.email ? (
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Mail className="w-3 h-3" />
                          {supplier.email}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-300">
                      {supplier.payment_terms}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        {supplier.lead_time_days} days
                      </div>
                    </td>
                    {/* Total Spent Column */}
                    <td className="px-3 py-2 text-right">
                      <div className="text-sm font-semibold text-emerald-400">
                        ${(supplier.total_spent || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </div>
                      {supplier.po_count > 0 && (
                        <span className="text-[10px] text-gray-500">
                          ({supplier.po_count} PO{supplier.po_count !== 1 ? 's' : ''})
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${
                        supplier.status === 'active' 
                          ? 'bg-emerald-900/30 text-emerald-400' 
                          : 'bg-gray-700 text-gray-400'
                      }`}>
                        {supplier.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleEdit(supplier)}
                          className="p-1 text-blue-400 hover:text-blue-300"
                          title="Edit"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        {supplier.status === 'active' ? (
                          <button
                            onClick={() => handleDelete(supplier.id)}
                            className="p-1 text-red-400 hover:text-red-300"
                            title="Deactivate"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivate(supplier.id)}
                            className="p-1 text-emerald-400 hover:text-emerald-300"
                            title="Activate"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuppliersManager;
